import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ExternalLink, Truck } from "lucide-react";
import { apiClient } from "@/lib/api";
import { orderDiscountLabel } from "@/lib/discounts";
import OrderTimeline from "@/components/OrderTimeline";

const STATUS_LABELS = {
  PRE_TRANSIT: "Label Created", TRANSIT: "In Transit", DELIVERED: "Delivered",
  RETURNED: "Returned", FAILURE: "Delivery Issue", UNKNOWN: "Pending Update",
};

export default function AccountOrderDetail() {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [trackInfo, setTrackInfo] = useState(null);

  useEffect(() => {
    apiClient.get(`/orders/${orderNumber}`).then((res) => {
      setOrder(res.data);
      if (res.data.tracking_number) {
        apiClient.get(`/shipping/track/${orderNumber}`).then((t) => setTrackInfo(t.data)).catch(() => {});
      }
    }).catch(() => {});
  }, [orderNumber]);

  if (!order) return <div className="max-w-3xl mx-auto px-6 py-20" data-testid="account-order-detail-loading"><div className="h-64 bg-slate-100 rounded-2xl animate-pulse" /></div>;

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-16" data-testid="account-order-detail-page">
      <Link to="/account" className="text-sm text-brandblue-600 font-semibold">&larr; Back to Account</Link>
      <h1 className="text-2xl font-display font-medium text-slate-900 mt-4 mb-1">{order.order_number}</h1>
      <p className="text-sm text-slate-500 mb-8">{new Date(order.created_at).toLocaleString()}</p>

      <div className="border border-slate-200 rounded-2xl p-6 space-y-4">
        <div className="pb-4 border-b border-slate-100">
          <OrderTimeline order={order} trackStatus={trackInfo?.status} />
        </div>
        <div className="flex justify-between text-sm"><span className="text-slate-500">Payment Status</span><span className={`font-semibold capitalize ${order.payment_status === "paid" ? "text-emerald-600" : "text-amber-600"}`} data-testid="order-detail-payment-status">{order.payment_status}</span></div>
        <div className="flex justify-between text-sm"><span className="text-slate-500">Fulfillment Status</span><span className="font-semibold capitalize text-slate-900" data-testid="order-detail-fulfillment-status">{order.fulfillment_status}</span></div>
        {order.shipping_carrier && <div className="flex justify-between text-sm"><span className="text-slate-500">Shipping Method</span><span className="font-semibold text-slate-900" data-testid="order-detail-shipping-method">{order.shipping_carrier} {order.shipping_service}</span></div>}
        {order.tracking_number && (
          <div className="pt-4 border-t border-slate-100" data-testid="order-detail-tracking-section">
            <p className="font-semibold text-slate-900 text-sm mb-2 flex items-center gap-2"><Truck className="h-4 w-4 text-brandblue-600" /> Shipment Tracking</p>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Tracking Number</span>
              {trackInfo?.tracking_url ? (
                <a href={trackInfo.tracking_url} target="_blank" rel="noopener noreferrer" className="font-semibold text-brandblue-600 hover:text-brandblue-800 inline-flex items-center gap-1" data-testid="order-detail-tracking-link">
                  {order.tracking_number} <ExternalLink className="h-3 w-3" />
                </a>
              ) : (
                <span className="font-semibold text-slate-900" data-testid="order-detail-tracking-number">{order.tracking_number}</span>
              )}
            </div>
            {order.tracking_carrier && order.tracking_carrier !== "shippo" && <div className="flex justify-between text-sm mt-1"><span className="text-slate-500">Carrier</span><span className="font-semibold text-slate-900 uppercase">{order.tracking_carrier}</span></div>}
            {trackInfo?.status && (
              <div className="flex justify-between text-sm mt-1">
                <span className="text-slate-500">Status</span>
                <span className={`font-semibold ${trackInfo.status === "DELIVERED" ? "text-emerald-600" : "text-brandblue-600"}`} data-testid="order-detail-tracking-status">{STATUS_LABELS[trackInfo.status] || trackInfo.status}</span>
              </div>
            )}
            {trackInfo?.eta && <div className="flex justify-between text-sm mt-1"><span className="text-slate-500">Estimated Delivery</span><span className="font-semibold text-slate-900">{new Date(trackInfo.eta).toLocaleDateString()}</span></div>}
            {trackInfo?.history?.length > 0 && (
              <div className="mt-3 space-y-1.5">
                {trackInfo.history.slice(-4).reverse().map((h, i) => (
                  <div key={`${h.date || "nodate"}-${h.status || "nostatus"}-${i}`} className="text-xs text-slate-500 flex justify-between gap-3">
                    <span className="truncate">{h.details || STATUS_LABELS[h.status] || h.status}{h.location ? ` — ${h.location}` : ""}</span>
                    {h.date && <span className="flex-shrink-0">{new Date(h.date).toLocaleDateString()}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="pt-4 border-t border-slate-100 text-sm">
          <p className="font-semibold text-slate-900 mb-1">Research Use Only Acknowledgment</p>
          <div className="flex justify-between text-slate-500">
            <span>Status</span>
            <span className="font-semibold text-emerald-600" data-testid="order-detail-ruo-status">{order.ruo_acknowledged ? "Accepted" : "Not Accepted"}</span>
          </div>
          {order.ruo_acknowledged_at && (
            <div className="flex justify-between text-slate-500">
              <span>Date &amp; Time</span>
              <span className="font-semibold text-slate-900" data-testid="order-detail-ruo-timestamp">{new Date(order.ruo_acknowledged_at).toLocaleString()}</span>
            </div>
          )}
        </div>

        <div className="pt-4 border-t border-slate-100 space-y-2">
          {order.items.map((item) => (
            <div key={item.product_id} className="flex justify-between text-sm text-slate-600">
              <span>{item.name} ({item.strength}) x{item.quantity}</span><span>${(item.price * item.quantity).toFixed(2)}</span>
            </div>
          ))}
        </div>

        <div className="pt-4 border-t border-slate-100 space-y-1.5 text-sm text-slate-600">
          <div className="flex justify-between"><span>Subtotal</span><span>${order.subtotal.toFixed(2)}</span></div>
          {order.discount > 0 && (
            <div className="flex justify-between text-emerald-600" data-testid="order-detail-discount">
              <span>{orderDiscountLabel(order)}</span>
              <span>-${order.discount.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between"><span>Shipping</span><span>${order.shipping_cost.toFixed(2)}</span></div>
          {order.tax_amount > 0 && <div className="flex justify-between"><span>Sales Tax ({order.tax_rate.toFixed(0)}%)</span><span data-testid="order-detail-tax">${order.tax_amount.toFixed(2)}</span></div>}
        </div>
        <div className="pt-4 border-t border-slate-100 flex justify-between font-display font-bold text-slate-900">
          <span>Total</span><span data-testid="order-detail-total">${order.total.toFixed(2)}</span>
        </div>

        <div className="pt-4 border-t border-slate-100 text-sm text-slate-600">
          <p className="font-semibold text-slate-900 mb-1">Shipping Address</p>
          <p>{order.shipping_address.full_name}</p>
          {order.shipping_address.company && <p data-testid="account-order-company">{order.shipping_address.company}</p>}
          <p>{order.shipping_address.line1}, {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.postal_code}</p>
          <p>{order.shipping_address.country}</p>
        </div>
      </div>
    </div>
  );
}
